import React, { useState, useRef, useEffect, useMemo } from 'react';
import UnsavedChangesDialog from '../components/UnsavedChangesDialog';
import '../styles/ScenarioManagement.css';
import {
  Box,
  Button,
  Typography,
  Paper,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Grid,
  Autocomplete } from
'@mui/material';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import api from '../services/api';
import SearchableDataTable from '../components/SearchableDataTable';
import { usePointInTimeFilter, filterDataByPointInTime } from '../hooks/usePointInTimeFilter';
import { COUNTRIES } from '../constants/dropdownOptions';

const ScenarioManagement = () => {
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const showNotification = (message, severity) => {
    setNotification({ open: true, message, severity });
  };

  // Validate scenario name uniqueness
  const validateScenarioNameUniqueness = (name) => {
    if (!name.trim()) {
      setScenarioNameError('');
      return true;
    }

    const isDuplicate = scenarios.some((scenario) =>
    scenario.ScenarioName.toLowerCase() === name.trim().toLowerCase() && (
    !editMode || scenario.id !== currentScenario.id)
    );

    if (isDuplicate) {
      setScenarioNameError('A scenario with this name already exists');
      return false;
    } else {
      setScenarioNameError('');
      return true;
    }
  };
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingScenario, setSavingScenario] = useState(false);
  const [deletingScenarios, setDeletingScenarios] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [scenarioType, setScenarioType] = useState('');
  const [templateScenarioId, setTemplateScenarioId] = useState('');
  const [templateScenario, setTemplateScenario] = useState(null);
  const [currentScenario, setCurrentScenario] = useState({
    ScenarioName: '',
    Year: new Date().getFullYear(),
    Period: '',
    Description: '',
    DomicileCountry: '',
    DomesticTaxRate: '',
    PointInTime: '',
    entityList: []
  });

  // Entity management state
  const [entities, setEntities] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);

  // Point In Time management state
  const [pointInTimes, setPointInTimes] = useState([]);
  const [loadingPointInTimes, setLoadingPointInTimes] = useState(false);

  // Get PointInTime filter from context
  const { selectedPointInTime } = usePointInTimeFilter();

  // Filter data based on selected PointInTime
  const filteredScenarios = useMemo(() => {
    if (!selectedPointInTime || selectedPointInTime === 'All') {
      return scenarios;
    }

    // Filter by PointInTime code
    return scenarios.filter((scenario) =>
    scenario.PointInTimeCode === selectedPointInTime
    );
  }, [scenarios, selectedPointInTime]);

  // Load scenarios when component mounts
  useEffect(() => {
    loadScenarios();
  }, []);

  // Reset scenario type when dialog opens and fetch entities
  useEffect(() => {
    if (openDialog) {
      setScenarioType('');
      setTemplateScenario(null);
      fetchEntities();
      fetchPointInTimes();
    }
  }, [openDialog]);

  // Fetch entities for the dropdown
  const fetchEntities = async () => {
    setLoadingEntities(true);
    try {
      const response = await api.get('/entity-models/master');
      setEntities((response.data.data || []).map((entity) => ({
        EntityCode: entity.EntityCode,
        EntityName: entity.EntityName,
        Year: entity.Year,
        Period: entity.Period,
        PointInTime: entity.PointInTime,
        PeriodInTime: `${entity.Year}-${entity.Period}`,
        label: `${entity.EntityCode} - ${entity.EntityName}`
      })));
    } catch (error) {
      console.error('Error fetching entities:', error);
    } finally {
      setLoadingEntities(false);
    }
  };

  // Fetch Point In Times for the dropdown
  const fetchPointInTimes = async () => {
    setLoadingPointInTimes(true);
    try {
      const response = await api.get('/point-in-times');
      setPointInTimes(response.data.data || []);
    } catch (error) {
      console.error('Error fetching point in times:', error);
    } finally {
      setLoadingPointInTimes(false);
    }
  };

  const loadScenarios = async () => {
    try {
      setLoading(true);
      const [scenariosResponse, pointInTimesResponse] = await Promise.all([
      api.get('/master-scenarios'),
      api.get('/point-in-times')]
      );

      // Create lookup map for PointInTime codes to names
      const pointInTimeMap = new Map();
      (pointInTimesResponse.data.data || []).forEach((pit) => {
        pointInTimeMap.set(pit.Code, pit.Name);
      });

      // Transform API data to match frontend schema
      const transformedScenarios = (scenariosResponse.data.data || []).map((scenario) => ({
        id: scenario.id,
        ScenarioCode: scenario.ScenarioCode,
        ScenarioName: scenario.ScenarioName,
        Year: scenario.Year,
        Period: scenario.Period,
        periodDisplay: `${scenario.Year}-${scenario.Period}`,
        ModifiedBy: scenario.ModifiedBy,
        ModifiedOn: new Date(scenario.ModifiedOn).toLocaleString(),
        Description: scenario.Description,
        DomicileCountry: scenario.DomicileCountry,
        DomesticTaxRate: scenario.DomesticTaxRate,
        PointInTime: pointInTimeMap.get(scenario.PointInTime) || scenario.PointInTime || '',
        PointInTimeCode: scenario.PointInTime || '' // Keep the original code for filtering
      }));
      setScenarios(transformedScenarios);
    } catch (error) {
      console.error('Error loading scenarios:', error);
      showNotification('Error loading scenarios', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ... rest of the component content ...

  // Handle adjustment duplication for copy scenarios
  const handleDuplicateAdjustments = async (newScenario) => {
    try {









      // Fetch all adjustments first
      const adjustmentsResponse = await api.get('/adjustments/master');


      // Filter adjustments on the frontend
      const filteredAdjustments = adjustmentsResponse.data.data.filter((adjustment) =>
      adjustment.Year === templateScenario.Year &&
      adjustment.Period === templateScenario.Period
      );



      // Prepare the adjustments to be duplicated with default values
      const adjustmentsToDuplicate = filteredAdjustments.map((adjustment) => ({
        AdjustmentName: adjustment.AdjustmentName || '',
        AdjustmentCode: adjustment.AdjustmentCode || '',
        AdjustmentType: adjustment.AdjustmentType || 'Standard',
        ASURollup: adjustment.ASURollup || '',
        FootnoteRollup: adjustment.FootnoteRollup || '',
        CustomRollup: adjustment.CustomRollup || '',
        Year: newScenario.Year,
        Period: newScenario.Period,
        ScenarioId: newScenario.id,
        ModifiedBy: 'admin',
        ModifiedOn: new Date().toISOString(),
        ScenarioCode: newScenario.ScenarioCode,
        dateCreated: new Date().toISOString()
      }));

      // Check for existing adjustments in the response
      const existingAdjustments = adjustmentsResponse.data.data.filter((adjustment) =>
      adjustment.Year === newScenario.Year &&
      adjustment.Period === newScenario.Period
      );








      // Filter out adjustments that already exist based on Name, Type, Year, and Period
      const adjustmentsToCreate = adjustmentsToDuplicate.filter((adjustment) => {







        const exists = existingAdjustments.some((existing) => {
          return existing.AdjustmentName === adjustment.AdjustmentName &&
          existing.AdjustmentType === adjustment.AdjustmentType &&
          existing.Year === adjustment.Year &&
          existing.Period === adjustment.Period;
        });


        return !exists;
      });



      // Create new adjustments
      if (adjustmentsToCreate.length > 0) {
        try {
          // Create adjustments one by one to handle errors individually
          for (const adjustment of adjustmentsToCreate) {
            await api.post('/adjustments/master', adjustment);
          }


        } catch (error) {
          console.error('Error creating adjustments:', error);
          throw new Error('Failed to create adjustments');
        }
      }
    } catch (error) {
      console.error('Error duplicating adjustments:', error);
      throw error;
    }
  };

  // Handle rollover of master data (MasterAdjustments, MasterEntities, MasterStates)
  // when the new scenario has a different PointInTime than the template scenario.
  const handleRolloverMasterData = async (newScenario) => {
    const templatePIT = templateScenario.PointInTime;
    const newPIT = newScenario.PointInTime;

    if (!templatePIT || !newPIT || templatePIT === newPIT) return;

    // --- MasterAdjustments ---
    try {
      const adjResponse = await api.get('/adjustments/master');
      const allAdj = adjResponse.data.data || [];
      const templateAdj = allAdj.filter((a) => a.PointInTime === templatePIT);
      const existingAdjCodes = new Set(
        allAdj.filter((a) => a.PointInTime === newPIT).map((a) => a.AdjustmentCode)
      );
      let adjCreated = 0;
      for (const adj of templateAdj) {
        if (existingAdjCodes.has(adj.AdjustmentCode)) continue;
        await api.post('/adjustments/master', {
          AdjustmentCode: adj.AdjustmentCode,
          AdjustmentName: adj.AdjustmentName,
          AdjustmentType: adj.AdjustmentType,
          PointInTime: newPIT,
          ModifiedBy: 'admin',
          ModifiedOn: new Date().toISOString(),
          dateCreated: new Date().toISOString()
        });
        existingAdjCodes.add(adj.AdjustmentCode);
        adjCreated++;
      }

    } catch (err) {
      console.error('Error rolling over MasterAdjustments:', err);
    }

    // --- MasterEntities ---
    try {
      const entResponse = await api.get('/entity-models/master');
      const allEnt = entResponse.data.data || [];
      const templateEnt = allEnt.filter((e) => e.PointInTime === templatePIT);
      const existingEntCodes = new Set(
        allEnt.filter((e) => e.PointInTime === newPIT).map((e) => e.EntityCode)
      );
      let entCreated = 0;
      for (const ent of templateEnt) {
        if (existingEntCodes.has(ent.EntityCode)) continue;
        await api.post('/entity-models/master', {
          EntityCode: ent.EntityCode,
          EntityName: ent.EntityName,
          EntityShortName: ent.EntityShortName || '',
          Region: ent.Region || 'Global',
          Country: ent.Country || 'United States',
          Currency: ent.Currency || 'USD',
          LegalType: ent.LegalType || '',
          TaxType: ent.TaxType || '',
          TaxSubType: ent.TaxSubType || '',
          Active: ent.Active !== undefined ? ent.Active : true,
          CountryOfInc: ent.CountryOfInc || '',
          CountryASU: ent.CountryASU || '',
          CountryCbCR: ent.CountryCbCR || '',
          CountryOECD: ent.CountryOECD || '',
          IsTopSide: ent.IsTopSide !== undefined ? ent.IsTopSide : false,
          IsTopSideUS: ent.IsTopSideUS !== undefined ? ent.IsTopSideUS : false,
          IsBranch: ent.IsBranch !== undefined ? ent.IsBranch : false,
          Segment: ent.Segment || '',
          TaxYearEnd: ent.TaxYearEnd || '',
          taxFilingEntityCode: ent.taxFilingEntityCode || '',
          TaxId: ent.TaxId || '',
          reportingBasis: ent.reportingBasis || 'Calendar',
          Ownership: ent.Ownership || '',
          PointInTime: newPIT,
          ModifiedBy: 'admin',
          ModifiedOn: new Date().toISOString(),
          dateCreated: new Date().toISOString()
        });
        existingEntCodes.add(ent.EntityCode);
        entCreated++;
      }

    } catch (err) {
      console.error('Error rolling over MasterEntities:', err);
    }

    // --- MasterStates ---
    try {
      const stateResponse = await api.get('/states/master');
      const allStates = stateResponse.data.data || [];
      const templateStates = allStates.filter((s) => s.PointInTime === templatePIT);
      const existingStateKeys = new Set(
        allStates.
        filter((s) => s.PointInTime === newPIT).
        map((s) => `${s.EntityCode}||${s.State}||${s.FilingType}||${s.TaxType}`)
      );
      let stateCreated = 0;
      for (const state of templateStates) {
        const key = `${state.EntityCode}||${state.State}||${state.FilingType}||${state.TaxType}`;
        if (existingStateKeys.has(key)) continue;
        await api.post('/states/master', {
          EntityCode: state.EntityCode,
          FilingType: state.FilingType,
          TaxType: state.TaxType,
          State: state.State,
          FilingName: state.FilingName,
          FilingCode: state.FilingCode,
          PointInTime: newPIT,
          entityList: state.entityList || [],
          ModifiedBy: 'admin',
          ModifiedOn: new Date().toISOString(),
          dateCreated: new Date().toISOString()
        });
        existingStateKeys.add(key);
        stateCreated++;
      }

    } catch (err) {
      console.error('Error rolling over MasterStates:', err);
    }
  };

  // Handle adjustment input rollover: compute pretax ending balance per adjustment group
  // and create a Beginning Balance record for the new scenario.
  // Amount = LC value (sum of all activity amounts), USDAmount = USD value (sum of all activity USDAmounts)
  const handleRolloverAdjustmentInputs = async (newScenario) => {
    // Only include adjustment types that appear in the deferred rollforward
    const ADJUSTMENT_INPUT_ROUTES = [
    'temp', // Temporary (STAT TO TAX TEMPORARY DIFFERENCES)
    'gs-temp', // GS Temporary (GAAP TO STAT TEMPORARY DIFFERENCES)
    'ti-temp', // TI Temporary (TAXABLE INCOME TEMP ADJUSTMENT)
    'net-operating-loss', // Net Operating Loss (NET OPERATING LOSS)
    'valuation-allowance', // Valuation Allowance (VALUATION ALLOWANCE)
    'tax-effected-temp', // Tax Effected Temporary (TAX EFFECTED TEMPORARY DIFFERENCES)
    'tax-effected-valuation-allowance' // Tax Effected Valuation Allowance (TAX EFFECTED VALUATION ALLOWANCE)
    ];

    let totalCreated = 0;

    for (const route of ADJUSTMENT_INPUT_ROUTES) {
      try {
        // Fetch template inputs and new scenario's existing inputs in parallel
        const [templateResponse, newResponse] = await Promise.all([
        api.get(`/adjustments/${route}`, { params: { scenarioCode: templateScenario.ScenarioCode } }),
        api.get(`/adjustments/${route}`, { params: { scenarioCode: newScenario.ScenarioCode } })]
        );

        const inputs = (templateResponse.data?.data || []).filter(
          (input) => input.ScenarioCode === templateScenario.ScenarioCode
        );

        if (inputs.length === 0) continue;

        const newScenarioRecords = (newResponse.data?.data || []).
        filter((r) => r.ScenarioCode === newScenario.ScenarioCode);

        // If the new scenario already has any records on this route, skip it entirely
        if (newScenarioRecords.length > 0) {

          continue;
        }

        // Build a set of AdjustmentCode+EntityCode keys that already exist in the new scenario
        const existingKeys = new Set(
          newScenarioRecords.map((r) => `${r.AdjustmentCode}||${r.EntityCode}`)
        );

        // Group by AdjustmentCode + EntityCode only.
        // Conflicting SubEntity/Tag/SubTag values across rows are discarded (set to empty / 'Base').
        const groups = {};
        for (const input of inputs) {
          const key = [input.AdjustmentCode, input.EntityCode].join('||');

          if (!groups[key]) {
            groups[key] = {
              AdjustmentCode: input.AdjustmentCode,
              EntityCode: input.EntityCode,
              SubEntity: 'Base',
              Tag: '',
              SubTag: '',
              PointInTime: input.PointInTime,
              totalAmount: 0,
              totalUSDAmount: 0
            };
          }
          groups[key].totalAmount += parseFloat(input.Amount) || 0;
          groups[key].totalUSDAmount += parseFloat(input.USDAmount) || 0;
        }

        // Create a Beginning Balance record for each group that has a non-zero ending balance
        // and does not already exist in the new scenario
        for (const group of Object.values(groups)) {
          if (group.totalAmount === 0 && group.totalUSDAmount === 0) continue;

          const key = `${group.AdjustmentCode}||${group.EntityCode}`;
          if (existingKeys.has(key)) continue;

          await api.post(`/adjustments/${route}`, {
            ScenarioCode: newScenario.ScenarioCode,
            EntityCode: group.EntityCode,
            SubEntity: group.SubEntity,
            AdjustmentCode: group.AdjustmentCode,
            ActivityType: 'BeginningBalance',
            Amount: group.totalAmount,
            USDAmount: group.totalUSDAmount,
            Tag: group.Tag,
            SubTag: group.SubTag,
            PointInTime: newScenario.PointInTime || group.PointInTime,
            modifiedBy: 'admin',
            ModifiedOn: new Date().toISOString(),
            dateCreated: new Date().toISOString()
          });
          existingKeys.add(key);
          totalCreated++;
        }
      } catch (err) {
        console.error(`Error rolling over adjustment inputs for route ${route}:`, err);
      }
    }


  };

  // Handle EntityInput rate rollover: map template scenario's ending rates to new scenario's beginning rates
  const handleRolloverEntityInputs = async (newScenario) => {
    try {
      // Fetch template scenario's EntityInputs
      const templateInputsResp = await api.get('/entity-models/input');
      const allInputs = templateInputsResp.data || [];
      const templateInputs = allInputs.filter(
        (input) => input.ScenarioCode === templateScenario.ScenarioCode
      );

      if (templateInputs.length === 0) return;

      // Fetch new scenario's EntityInputs (just created by auto-creation)
      const newInputs = allInputs.filter(
        (input) => input.ScenarioCode === newScenario.ScenarioCode
      );

      for (const templateInput of templateInputs) {
        const newInput = newInputs.find((i) => i.EntityCode === templateInput.EntityCode);
        if (!newInput) continue;

        // Map ending rates from template → beginning rates for new scenario
        // Copy current/end/average rates from template as a starting point for the new period
        await api.put(`/entity-models/input/${newInput.id}`, {
          BeginningDeferredTaxRate: templateInput.EndingDeferredTaxRate,
          PriorCurrentTaxRate: templateInput.CurrentTaxRate,
          BeginningSpotRate: templateInput.EndingSpotRate,
          EndingDeferredTaxRate: templateInput.EndingDeferredTaxRate,
          CurrentTaxRate: templateInput.CurrentTaxRate,
          AverageFXRate: templateInput.AverageFXRate,
          EndingSpotRate: templateInput.EndingSpotRate,
          modifiedBy: 'admin',
          ModifiedOn: new Date().toISOString()
        });
      }


    } catch (err) {
      console.error('Error rolling over EntityInput rates:', err);
    }
  };

  // Handle entity duplication for copy scenarios
  const handleDuplicateEntities = async (newScenario) => {
    try {
      // Fetch all entities first
      const entitiesResponse = await api.get('/entity-models/master');

      // Filter entities on the frontend by template PointInTime
      const filteredEntities = entitiesResponse.data.data.filter((entity) =>
      entity.PointInTime === templateScenario.PointInTime
      );



      // Prepare the entities to be duplicated with the new PointInTime
      const entitiesToDuplicate = filteredEntities.map((entity) => ({
        EntityCode: entity.EntityCode || '',
        EntityName: entity.EntityName || 'New Entity',
        EntityShortName: entity.EntityShortName || '',
        Region: entity.Region || 'Global',
        Country: entity.Country || 'United States',
        Currency: entity.Currency || 'USD',
        LegalType: entity.LegalType || 'Corporation',
        TaxType: entity.TaxType || 'Disregarded Entity',
        TaxSubType: entity.TaxSubType || 'DRE',
        Active: entity.Active !== undefined ? entity.Active : true,
        CountryOfInc: entity.CountryOfInc || '',
        CountryASU: entity.CountryASU || '',
        CountryCbCR: entity.CountryCbCR || '',
        CountryOECD: entity.CountryOECD || '',
        PointInTime: newScenario.PointInTime || entity.PointInTime,
        ModifiedBy: 'admin',
        ModifiedOn: new Date().toISOString(),
        IsTopSide: entity.IsTopSide !== undefined ? entity.IsTopSide : false,
        IsTopSideUS: entity.IsTopSideUS !== undefined ? entity.IsTopSideUS : false,
        IsBranch: entity.IsBranch !== undefined ? entity.IsBranch : false,
        Segment: entity.Segment || '',
        TaxYearEnd: entity.TaxYearEnd || '',
        taxFilingEntityCode: entity.taxFilingEntityCode || '',
        TaxId: entity.TaxId || '',
        reportingBasis: entity.reportingBasis || 'Calendar',
        Ownership: entity.Ownership || ''
      }));

      // Dedupe by EntityCode against existing records for the new PointInTime
      const existingEntCodes = new Set(
        entitiesResponse.data.data.
        filter((e) => e.PointInTime === (newScenario.PointInTime || templateScenario.PointInTime)).
        map((e) => e.EntityCode)
      );

      const entitiesToCreate = entitiesToDuplicate.filter((entity) => {
        if (existingEntCodes.has(entity.EntityCode)) return false;
        existingEntCodes.add(entity.EntityCode);
        return true;
      });



      for (const entity of entitiesToCreate) {
        await api.post('/entity-models/master', entity);
      }

      // Show success message
      showNotification('Entities duplicated successfully', 'success');
    } catch (error) {
      console.error('Error duplicating entities:', error);
      if (error.response) {
        console.error('API response:', error.response.data);
      }
      showNotification(`Error duplicating entities: ${error.message || 'Unknown error'}`, 'error');
    }
  };

  const loadTemplateScenario = async (scenarioId) => {
    try {
      const response = await api.get(`/master-scenarios/${scenarioId}`);
      const templateData = response.data.data;
      const nameSuffix = scenarioType === 'rollover' ? ' Rollover' : ' Copy';
      setCurrentScenario({
        ScenarioName: `${templateData.ScenarioName}${nameSuffix}`,
        Year: templateData.Year,
        Period: templateData.Period,
        Description: templateData.Description,
        DomicileCountry: templateData.DomicileCountry,
        DomesticTaxRate: templateData.DomesticTaxRate,
        PointInTime: '',
        entityList: templateData.entityList || []
      });
      setTemplateScenario(templateData);
      validateScenarioNameUniqueness(`${templateData.ScenarioName}${nameSuffix}`);
    } catch (error) {
      console.error('Error loading template scenario:', error);
      showNotification('Error loading template scenario', 'error');
    }
  };

  const handleScenarioTypeChange = (e) => {
    setScenarioType(e.target.value);
    setScenarioNameError('');
    if (e.target.value === 'blank') {
      setCurrentScenario({
        ScenarioName: '',
        Year: new Date().getFullYear(),
        Period: '',
        Description: '',
        DomicileCountry: '',
        DomesticTaxRate: '',
        PointInTime: '',
        entityList: []
      });
      setTemplateScenarioId('');
    } else if (e.target.value === 'copy' || e.target.value === 'rollover') {
      setCurrentScenario({
        ScenarioName: '',
        Year: new Date().getFullYear(),
        Period: '',
        Description: '',
        DomicileCountry: '',
        DomesticTaxRate: '',
        PointInTime: '',
        entityList: []
      });
    } else {
      setCurrentScenario({
        ScenarioName: '',
        Year: new Date().getFullYear(),
        Period: '',
        Description: '',
        DomicileCountry: '',
        DomesticTaxRate: '',
        PointInTime: '',
        entityList: []
      });
    }
  };

  // State for import dialog
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  // State for row selection
  const [selectedRows, setSelectedRows] = useState([]);
  const [scenarioNameError, setScenarioNameError] = useState('');

  // Handle state duplication for copy scenarios
  const handleDuplicateStates = async (newScenario, templateScenario) => {
    try {





      // Fetch all states first
      const statesResponse = await api.get('/states/master');

      // Filter states on the frontend by template PointInTime
      const filteredStates = statesResponse.data.data.filter((state) =>
      state.PointInTime === templateScenario.PointInTime
      );



      // Prepare the states to be duplicated with the new PointInTime
      const statesToDuplicate = filteredStates.map((state) => ({
        EntityCode: state.EntityCode,
        FilingType: state.FilingType,
        TaxType: state.TaxType,
        State: state.State,
        FilingName: state.FilingName,
        FilingCode: state.FilingCode,
        PointInTime: newScenario.PointInTime || state.PointInTime,
        entityList: state.entityList || [],
        ModifiedBy: 'admin'
      }));

      // Check for existing states with the new PointInTime — dedupe by EntityCode+State+FilingType+TaxType
      const existingStateKeys = new Set(
        statesResponse.data.data.
        filter((s) => s.PointInTime === (newScenario.PointInTime || templateScenario.PointInTime)).
        map((s) => `${s.EntityCode}||${s.State}||${s.FilingType}||${s.TaxType}`)
      );

      // Filter out states that already exist
      const statesToCreate = statesToDuplicate.filter((state) => {
        const key = `${state.EntityCode}||${state.State}||${state.FilingType}||${state.TaxType}`;
        if (existingStateKeys.has(key)) return false;
        existingStateKeys.add(key);
        return true;
      });



      // Create new states
      for (const state of statesToCreate) {
        await api.post('/states/master', state);
      }


    } catch (error) {
      console.error('Error duplicating states:', error);
      showNotification(`Failed to duplicate states: ${error.message || 'Unknown error'}`, 'error');
    }
  };

  // Handle adding a new scenario
  const handleAddNew = () => {
    setEditMode(false);
    setCurrentScenario({
      ScenarioName: '',
      Year: null,
      Period: '',
      Description: '',
      DomicileCountry: '',
      DomesticTaxRate: '',
      PointInTime: '',
      entityList: []
    });
    setScenarioNameError('');
    setOpenDialog(true);
  };

  // Handle saving a scenario
  const handleSave = async () => {
    let newScenario;
    setSavingScenario(true);
    try {
      // Validate scenario name uniqueness
      if (!validateScenarioNameUniqueness(currentScenario.ScenarioName)) {
        setSavingScenario(false);
        return;
      }

      // Validate Domestic Tax Rate is provided and valid
      const rateVal = currentScenario.DomesticTaxRate;
      if (rateVal === '' || rateVal === null || rateVal === undefined || isNaN(Number(rateVal))) {
        showNotification('Please enter a valid Domestic Tax Rate before saving.', 'warning');
        setSavingScenario(false);
        return;
      }

      // Derive Year/Period from PointInTime if available
      const deriveYearPeriodFromPIT = (code) => {
        if (!code) return { year: null, period: '' };
        const m = String(code).toUpperCase().match(/^(\d{4})-(Q[1-4]|ANNUAL)$/);
        if (m) return { year: parseInt(m[1], 10), period: m[2] };
        return { year: new Date().getFullYear(), period: 'Q4' };
      };

      // Transform frontend scenario to match backend schema



      const entityCodes = (currentScenario.entityList || []).map((item) =>
      typeof item === 'object' ? item.EntityCode : item
      );


      const { year: derivedYear, period: derivedPeriod } = deriveYearPeriodFromPIT(currentScenario.PointInTime);
      const scenarioData = {
        ScenarioName: currentScenario.ScenarioName,
        Year: derivedYear,
        Period: derivedPeriod,
        PointInTime: currentScenario.PointInTime,
        ModifiedBy: 'admin',
        Description: currentScenario.Description,
        DomicileCountry: currentScenario.DomicileCountry,
        DomesticTaxRate: currentScenario.DomesticTaxRate === '' ? null : parseFloat(currentScenario.DomesticTaxRate),
        entityList: entityCodes
      };

      // Only include ScenarioCode when updating (editMode)
      if (editMode) {
        scenarioData.ScenarioCode = currentScenario.ScenarioCode;
      }

      if (editMode) {
        // Update existing scenario


        await api.put(`/master-scenarios/${currentScenario.id}`, scenarioData);

        // Auto-create EntityInput for any new entities added during edit
        try {
          const rawEntityCodes = (currentScenario.entityList || []).map((item) =>
          typeof item === 'object' ? item.EntityCode : item
          );
          const entityCodes = Array.from(new Set(rawEntityCodes));

          if (entityCodes.length > 0 && currentScenario.ScenarioCode) {
            // Fetch master entities to check active status
            const masterEntitiesResponse = await api.get('/entity-models/master');
            const masterEntities = masterEntitiesResponse.data.data || [];

            // Fetch existing EntityInputs to avoid duplicates
            const existingInputsResp = await api.get('/entity-models/input');
            const existingInputs = existingInputsResp.data || [];
            const existingForScenario = new Set(
              existingInputs.
              filter((input) => input.ScenarioCode === currentScenario.ScenarioCode).
              map((input) => input.EntityCode)
            );

            for (const code of entityCodes) {
              const details = masterEntities.find((e) => e.EntityCode === code);
              if (!details) {

                continue;
              }
              if (!details.Active) {

                continue;
              }
              if (existingForScenario.has(code)) {
                // Skip if EntityInput already exists
                continue;
              }

              // Create EntityInput
              try {
                const { year: eiYear, period: eiPeriod } = function derive(pitCode) {
                  if (!pitCode) return { year: null, period: '' };
                  const m = String(pitCode).toUpperCase().match(/^(\d{4})-(Q[1-4]|ANNUAL)$/);
                  if (m) return { year: parseInt(m[1], 10), period: m[2] };
                  return { year: new Date().getFullYear(), period: 'Q4' };
                }(currentScenario.PointInTime);

                await api.post('/entity-models/input', {
                  ScenarioCode: currentScenario.ScenarioCode,
                  EntityCode: code,
                  CurrentTaxRate: null,
                  BeginningDeferredTaxRate: null,
                  EndingDeferredTaxRate: null,
                  AverageFXRate: null,
                  BeginningSpotRate: null,
                  EndingSpotRate: null,
                  Year: eiYear,
                  Period: eiPeriod,
                  dateCreated: new Date().toISOString(),
                  ModifiedOn: new Date().toISOString(),
                  modifiedBy: 'admin'
                });

                existingForScenario.add(code);
              } catch (eiErr) {
                console.error('Failed to create EntityInput for', code, eiErr);
              }
            }
          }
        } catch (inputErr) {
          console.error('Error during EntityInput auto-creation on scenario update:', inputErr);
        }

        // Refresh the scenarios list
        await loadScenarios();

        showNotification('Scenario updated successfully', 'success');
      } else {
        // Create new scenario
        const response = await api.post('/master-scenarios', scenarioData);
        newScenario = response.data.data;

        // Show success notification after getting the new scenario ID
        showNotification('Scenario created successfully', 'success');

        // If this is a copy or rollover scenario, duplicate entities, states, and adjustments FIRST
        if ((scenarioType === 'copy' || scenarioType === 'rollover') && templateScenario) {
          await handleDuplicateEntities(newScenario);
          await handleDuplicateStates(newScenario, templateScenario);
          await handleDuplicateAdjustments(newScenario);
        }

        // For rollover scenarios, copy master data (adjustments, entities, states) with updated PointInTime
        if (scenarioType === 'rollover' && templateScenario) {
          await handleRolloverMasterData(newScenario);
        }

        // Auto-create EntityInput (and trigger StateInput auto-creation) for associated entities on creation
        // For copy scenarios, this happens AFTER entity duplication to ensure MasterEntity records exist
        try {
          const rawEntityCodes = (currentScenario.entityList || []).map((item) =>
          typeof item === 'object' ? item.EntityCode : item
          );
          // Deduplicate entity codes from the UI list
          const entityCodes = Array.from(new Set(rawEntityCodes));






          if (entityCodes.length > 0 && newScenario?.ScenarioCode) {
            // Fetch master entities to check active status
            const masterEntitiesResponse = await api.get('/entity-models/master');
            const masterEntities = masterEntitiesResponse.data.data || [];


            // Fetch existing EntityInputs once and build a set for quick duplicate checks
            const existingInputsResp = await api.get('/entity-models/input');
            const existingInputs = existingInputsResp.data || [];



            const existingForScenario = new Set(
              existingInputs.
              filter((input) => input.ScenarioCode === newScenario.ScenarioCode).
              map((input) => input.EntityCode)
            );

            let created = 0;
            let skipped = 0;
            let notFound = 0;
            let inactive = 0;

            for (const code of entityCodes) {

              const details = masterEntities.find((e) => e.EntityCode === code);
              if (!details) {

                notFound++;
                continue;
              }


              if (!details.Active) {

                inactive++;
                continue;
              }
              if (existingForScenario.has(code)) {

                skipped++;
                continue;
              }

              // Create EntityInput; backend fills Year/Period from Scenario and auto-creates StateInputs
              try {

                await api.post('/entity-models/input', {
                  ScenarioCode: newScenario.ScenarioCode,
                  EntityCode: code,
                  modifiedBy: 'admin'
                });

                created++;
                existingForScenario.add(code);
              } catch (eiErr) {
                console.error('✗ Failed to create EntityInput for', code, eiErr);
                if (eiErr.response) {
                  console.error('Error response:', eiErr.response.data);
                }
              }
            }







          }
        } catch (inputErr) {
          console.error('Error during EntityInput auto-creation on scenario create:', inputErr);
        }

        // For rollover scenarios, update new EntityInputs with mapped rates from template BEFORE
        // creating adjustment inputs, so the correct rates are in place when adjustments reference them
        if (scenarioType === 'rollover' && templateScenario) {
          await handleRolloverEntityInputs(newScenario);
        }

        // For rollover scenarios, create Beginning Balance adjustment inputs from template's Ending Balance inputs.
        // This must run AFTER EntityInputs are created and rates are mapped so tax/FX rates are correct.
        if (scenarioType === 'rollover' && templateScenario) {
          await handleRolloverAdjustmentInputs(newScenario);
        }
      }

      await loadScenarios();
    } catch (error) {
      console.error('Error saving scenario:', error);
      showNotification('Error saving scenario', 'error');
    } finally {
      setSavingScenario(false);
      handleCloseDialog();
    }
  };

  // Handle deleting selected scenarios
  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedRows.length} selected scenarios?`)) {
      setDeletingScenarios(true);
      try {
        // Delete multiple scenarios
        const deletePromises = selectedRows.map((id) => api.delete(`/master-scenarios/${id}`));

        await Promise.all(deletePromises);

        // Refresh the scenarios list
        await loadScenarios();

        setSelectedRows([]);
        showNotification(`Successfully deleted ${selectedRows.length} scenarios`, 'success');
      } catch (error) {
        console.error('Error deleting scenarios:', error);
        showNotification('Failed to delete scenarios', 'error');
      } finally {
        setDeletingScenarios(false);
      }
    }
  };

  // Handle deleting a single scenario
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this scenario?')) {
      try {
        await api.delete(`/master-scenarios/${id}`);

        // Refresh the scenarios list
        await loadScenarios();

        showNotification('Scenario deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting scenario:', error);
        showNotification('Failed to delete scenario', 'error');
      }
    }
  };

  // Handle notification close
  const handleNotificationClose = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  // Handle selection change
  const handleSelectionChange = (newSelectionModel) => {
    setSelectedRows(newSelectionModel);
  };

  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const handleRequestClose = () => setConfirmCloseOpen(true);

  // Handle closing dialogs
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setConfirmCloseOpen(false);
  };

  // Handle import button click
  const handleImport = () => {
    fileInputRef.current.click();
  };

  // Handle file selection for import
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setImporting(true);
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        // Get first worksheet
        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          throw new Error('The Excel file is empty');
        }

        // Fetch PointInTime master data to map names to codes
        const pointInTimesResponse = await api.get('/point-in-times');
        const pointInTimes = pointInTimesResponse.data.data || [];

        // Create a map from PointInTime Name to Code (case-insensitive)
        const pitNameToCodeMap = new Map();
        pointInTimes.forEach((pit) => {
          pitNameToCodeMap.set(pit.Name.toLowerCase().trim(), pit.Code);
          // Also map the code to itself in case Excel has codes
          pitNameToCodeMap.set(pit.Code.toLowerCase().trim(), pit.Code);
        });

        // Get all column names from the first row, case-insensitive
        const fileColumns = Object.keys(jsonData[0] || {});
        const fileColumnsLower = fileColumns.map((col) => col.toLowerCase());

        // Map frontend column names to possible Excel column names
        const columnMappings = {
          'ScenarioCode': ['scenario code', 'scenariocode', 'code'],
          'ScenarioName': ['scenario name', 'scenarioname', 'name'],
          'PointInTime': ['point in time', 'pointintime', 'pit', 'period'],
          'DomicileCountry': ['domicile country', 'domicilecountry', 'country'],
          'DomesticTaxRate': ['domestic tax rate', 'domestictaxrate', 'tax rate', 'taxrate'],
          'Description': ['description', 'desc'],
          'ModifiedBy': ['modified by', 'modifiedby'],
          'ModifiedOn': ['modified on', 'modifiedon', 'date modified']
        };

        // Find matching columns in the Excel file
        const matchedColumns = {};
        let missingColumns = [];

        for (const [field, possibleNames] of Object.entries(columnMappings)) {
          const found = fileColumnsLower.some((col, index) => {
            if (possibleNames.includes(col.toLowerCase())) {
              matchedColumns[field] = fileColumns[index];
              return true;
            }
            return false;
          });

          if (!found && ['ScenarioName', 'PointInTime'].includes(field)) {
            missingColumns.push(possibleNames[0]);
          }
        }

        if (missingColumns.length > 0) {
          throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
        }

        // Get all existing scenarios to check for duplicates (by name)
        const allScenariosResponse = await api.get('/master-scenarios');
        const existingScenarios = allScenariosResponse.data.data || [];
        const scenariosMap = new Map(
          existingScenarios.map((s) => [s.ScenarioName.toLowerCase(), s])
        );

        // Process each row and track results
        const importResults = {
          created: 0,
          updated: 0,
          errors: []
        };

        // Process rows sequentially to avoid race conditions
        for (let i = 0; i < jsonData.length; i++) {
          try {
            const row = jsonData[i];

            // Extract values using matched column names
            const scenarioName = String(row[matchedColumns.ScenarioName] || '').trim();
            const pointInTimeInput = String(row[matchedColumns.PointInTime] || '').trim();
            const domicileCountryInput = String(row[matchedColumns.DomicileCountry] || '').trim();
            const domesticTaxRate = String(row[matchedColumns.DomesticTaxRate] || '').trim();
            const description = String(row[matchedColumns.Description] || '').trim();

            // Normalize country name to match COUNTRIES constant (case-insensitive)
            let domicileCountry = domicileCountryInput;
            if (domicileCountryInput) {
              const matchedCountry = COUNTRIES.find(
                (country) => country.toLowerCase() === domicileCountryInput.toLowerCase()
              );
              if (matchedCountry) {
                domicileCountry = matchedCountry;
              }
            }

            // Validate required fields
            if (!scenarioName) {
              throw new Error('Scenario Name is required');
            }
            if (!pointInTimeInput) {
              throw new Error('Point In Time is required');
            }

            // Map PointInTime Name to Code (case-insensitive lookup)
            const pointInTimeCode = pitNameToCodeMap.get(pointInTimeInput.toLowerCase().trim());

            if (!pointInTimeCode) {
              throw new Error(`Invalid Point In Time: "${pointInTimeInput}". Must match a valid PointInTime Name or Code.`);
            }

            // Derive Year/Period from PointInTime for backward compatibility
            const deriveYearPeriodFromPIT = (code) => {
              if (!code) return { year: null, period: '' };
              const m = String(code).toUpperCase().match(/^(\d{4})-(Q[1-4]|ANNUAL)$/);
              if (m) return { year: parseInt(m[1], 10), period: m[2] };
              return { year: new Date().getFullYear(), period: 'Q4' };
            };

            const { year: derivedYear, period: derivedPeriod } = deriveYearPeriodFromPIT(pointInTimeCode);

            const scenarioData = {
              ScenarioName: scenarioName,
              Year: derivedYear,
              Period: derivedPeriod,
              PointInTime: pointInTimeCode, // Store the CODE, not the name
              DomicileCountry: domicileCountry,
              DomesticTaxRate: domesticTaxRate ? parseFloat(domesticTaxRate) : null,
              Description: description,
              ModifiedBy: row[matchedColumns.ModifiedBy] || 'admin',
              ModifiedOn: row[matchedColumns.ModifiedOn] ? new Date(row[matchedColumns.ModifiedOn]) : new Date()
            };

            // Check if scenario with this name already exists
            const existingScenario = scenariosMap.get(scenarioName.toLowerCase());

            if (existingScenario) {
              // Update existing scenario - include ScenarioCode for update
              scenarioData.ScenarioCode = existingScenario.ScenarioCode;
              await api.put(`/master-scenarios/${existingScenario.id}`, scenarioData);
              importResults.updated++;
            } else {
              // Create new scenario - backend will auto-generate ScenarioCode
              await api.post('/master-scenarios', scenarioData);
              importResults.created++;
            }

          } catch (error) {
            console.error(`Error processing row ${i + 1}:`, jsonData[i], error);
            const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
            importResults.errors.push(`Row ${i + 1}: ${errorMessage}`);
          }
        }

        // Check if there were any errors
        if (importResults.errors.length > 0) {
          const errorMessage = importResults.errors.length === jsonData.length ?
          'All rows had errors. No data was imported.' :
          `Completed with ${importResults.errors.length} error(s) out of ${jsonData.length} rows.`;

          if (importResults.created === 0 && importResults.updated === 0) {
            throw new Error(`Import failed: ${errorMessage}\n\n${importResults.errors.join('\n')}`);
          } else {
            // Some rows succeeded, show warning
            setNotification({
              open: true,
              message: `Partial import: ${importResults.created} created, ${importResults.updated} updated, ${importResults.errors.length} errors`,
              severity: 'warning',
              autoHideDuration: 10000
            });
          }
        }

        // Refresh data
        await loadScenarios();

        // Only show success message if no errors were shown before
        if (importResults.errors.length === 0) {
          const updatedMsg = importResults.updated > 0 ? `, updated ${importResults.updated}` : '';
          setNotification({
            open: true,
            message: `Successfully imported ${importResults.created} scenario${importResults.created !== 1 ? 's' : ''}${updatedMsg}`,
            severity: 'success',
            autoHideDuration: 5000
          });
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error importing scenarios:', error);
      setNotification({
        open: true,
        message: error.message || 'Failed to import scenarios',
        severity: 'error',
        autoHideDuration: 10000
      });
    } finally {
      setImporting(false);
      // Clear the file input
      if (event.target) {
        event.target.value = null;
      }
    }
  };

  // Handle export button click - Export scenarios to Excel
  const handleExport = () => {
    try {
      // Determine which scenarios to export based on selection
      const scenariosToExport = selectedRows.length > 0 ?
      filteredScenarios.filter((scenario) => selectedRows.includes(scenario.id)) :
      filteredScenarios;

      // Filter out the actions column and map to export format
      const exportColumns = scenarioColumns.
      filter((col) => col.field !== 'actions' && col.headerName !== 'ACTIONS').
      map((col) => ({
        field: col.field,
        headerName: col.headerName || col.field
      }));

      // Create export data with only the columns we want to export
      const exportData = scenariosToExport.map((scenario) => {
        const row = {};
        exportColumns.forEach((col) => {
          row[col.headerName] = scenario[col.field];
        });
        return row;
      });

      // Create a worksheet from the exported data
      // Always include headers even if no data by specifying header option
      const worksheet = XLSX.utils.json_to_sheet(exportData, {
        header: exportColumns.map((col) => col.headerName)
      });

      // Auto-size columns
      const columnWidths = exportColumns.map((col) => ({
        wch: Math.max(
          col.headerName ? col.headerName.length : 0,
          ...exportData.map((row) => String(row[col.headerName] || '').length)
        ) + 2 // Add some padding
      }));

      worksheet['!cols'] = columnWidths;

      // Create a workbook and add the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Scenarios');

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array', bookSST: true });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      // Save the file
      saveAs(data, `Scenarios_Export_${new Date().toISOString().slice(0, 10)}.xlsx`);

      setNotification({
        open: true,
        message: `Successfully exported ${scenariosToExport.length} scenario${scenariosToExport.length !== 1 ? 's' : ''}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error exporting scenarios:', error);
      setNotification({
        open: true,
        message: 'Failed to export scenarios: ' + (error.message || 'Unknown error'),
        severity: 'error'
      });
    }
  };

  const scenarioColumns = [
  {
    field: 'ScenarioCode',
    headerName: 'Scenario Code',
    flex: 0.8,
    minWidth: 150,
    renderCell: (params) =>
    <div style={{ fontWeight: 'bold' }}>
          {params.value}
        </div>

  },
  {
    field: 'ScenarioName',
    headerName: 'Scenario Name',
    flex: 1,
    minWidth: 200
  },
  {
    field: 'DomicileCountry',
    headerName: 'Domicile Country',
    flex: 0.8,
    minWidth: 120
  },
  {
    field: 'DomesticTaxRate',
    headerName: 'Domestic Tax Rate',
    flex: 0.8,
    minWidth: 120,
    renderCell: (params) =>
    <div style={{ textAlign: 'right' }}>
          {params.value !== undefined ? `${params.value}%` : ''}
        </div>

  },
  {
    field: 'PointInTime',
    headerName: 'Point In Time',
    flex: 0.7,
    minWidth: 110
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 100,
    sortable: false,
    disableClickEventBubbling: true,
    renderCell: (params) =>
    <Box>
          <IconButton size="small" onClick={async () => {
        setEditMode(true);

        // Load associated entities for this scenario
        let associatedEntities = [];
        try {
          const [entitiesResponse, pointInTimesResponse] = await Promise.all([
          api.get('/entity-models/master'),
          api.get('/point-in-times')]
          );
          const allEntities = entitiesResponse.data.data || [];
          const allPointInTimes = pointInTimesResponse.data.data || [];

          // Find the PointInTime name for this scenario
          const scenarioPointInTime = allPointInTimes.find((pit) => pit.Code === params.row.PointInTimeCode);
          const scenarioPointInTimeName = scenarioPointInTime?.Name;

          // Get the scenario's entityList from the backend
          const scenarioResponse = await api.get(`/master-scenarios/${params.row.id}`);
          const scenarioEntityList = scenarioResponse.data.data.entityList || [];

          // Filter entities that are in the scenario's entityList AND match this scenario's PointInTime
          associatedEntities = allEntities.
          filter((entity) =>
          scenarioEntityList.includes(entity.EntityCode) &&
          entity.PointInTime === params.row.PointInTimeCode
          ).
          map((entity) => ({
            EntityCode: entity.EntityCode,
            EntityName: entity.EntityName,
            Year: entity.Year,
            Period: entity.Period,
            PointInTime: entity.PointInTime,
            PeriodInTime: `${entity.Year}-${entity.Period}`,
            label: `${entity.EntityCode} - ${entity.EntityName}`
          }));
        } catch (error) {
          console.error('Error loading entities for scenario:', error);
        }

        setCurrentScenario({
          id: params.row.id,
          ScenarioCode: params.row.ScenarioCode,
          ScenarioName: params.row.ScenarioName,
          Year: params.row.Year,
          Period: params.row.Period,
          PointInTime: params.row.PointInTimeCode,
          Description: params.row.Description,
          DomicileCountry: params.row.DomicileCountry,
          DomesticTaxRate: parseFloat(params.row.DomesticTaxRate),
          entityList: associatedEntities
        });
        setScenarioNameError('');
        setOpenDialog(true);
      }} title="Edit Scenario">
            <img src="/assets/Icons/icon_pencil.svg" alt="Edit" style={{ width: 18, height: 18 }} />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(params.row.id)} title="Delete Scenario">
            <img src="/assets/Icons/icon_trash.svg" alt="Delete" style={{ width: 18, height: 18 }} />
          </IconButton>
        </Box>

  }];


  return (
    <Box className="scenario-management-container">
      {/* Main Content */}
      <Box className="main-content">
        <Box className="toolbar-container">
          <Box className="selection-info" sx={{ pl: 0 }}>
            <Typography variant="h6" className="page-title" sx={{ pl: 0, fontWeight: 'bold' }}>
              Manage Scenarios
            </Typography>
            <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
              {selectedRows.length > 0 ? `${selectedRows.length} selected` : ''}
            </Typography>
          </Box>
          
          {/* Action Buttons */}
          <Box className="button-group">
            <Button
              variant="outlined"
              size="small"
              className="action-button"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={scenarios.length === 0}>

              Export
            </Button>
            <Button
              variant="outlined"
              size="small"
              className="action-button"
              startIcon={<UploadIcon />}
              onClick={handleImport}>

              Import
            </Button>
            <Button
              variant="outlined"
              size="small"
              className="action-button"
              startIcon={<AddIcon />}
              onClick={handleAddNew}>

              Add
            </Button>
            <Button
              variant="outlined"
              size="small"
              className="action-button"
              startIcon={deletingScenarios ? <CircularProgress size={16} color="inherit" /> : <RemoveIcon />}
              onClick={handleDeleteSelected}
              disabled={selectedRows.length === 0 || deletingScenarios}
              sx={{
                '&.Mui-disabled': {
                  opacity: 0.4
                }
              }}>

              {deletingScenarios ? 'Deleting...' : 'Remove'}
            </Button>
          </Box>
        </Box>

        {/* Data Table */}
        <Box className="table-container">
          {loading ?
          <LinearProgress /> :

          <SearchableDataTable
            searchPlaceholder="Search Scenarios..."
            rows={filteredScenarios}
            columns={scenarioColumns}
            loading={loading}
            onSelectionModelChange={handleSelectionChange}
            selectionModel={selectedRows}
            checkboxSelection={true}
            disableSelectionOnClick={true}
            pageSize={15}
            tableName="scenarios-table"
            defaultSortField="ScenarioName"
            defaultSortOrder="asc"
            onRefresh={loadScenarios}
            height="100%"
            className="custom-datagrid" />

          }
        </Box>
        
        {/* Add/Edit Dialog */}
        <UnsavedChangesDialog
          open={confirmCloseOpen}
          onConfirm={handleCloseDialog}
          onCancel={() => setConfirmCloseOpen(false)} />

        <Dialog open={openDialog} onClose={handleRequestClose} maxWidth="md" fullWidth>
          <DialogTitle>{editMode ? 'Edit Scenario' : 'Add New Scenario'}</DialogTitle>
          <DialogContent sx={{ pt: 2, maxHeight: '70vh', overflowY: 'auto' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {!editMode &&
              <TextField
                select
                fullWidth
                margin="dense"
                value={scenarioType}
                onChange={handleScenarioTypeChange}
                required
                label="Scenario Type">

                  <MenuItem value="copy">Copy From Existing Scenario</MenuItem>
                  <MenuItem value="rollover">Rollover Scenario</MenuItem>
                  <MenuItem value="blank">Create a New Blank Scenario</MenuItem>
                </TextField>
              }

              {editMode &&
              <>
                  <TextField
                  label="Scenario Code"
                  fullWidth
                  margin="dense"
                  value={currentScenario.ScenarioCode || ''}
                  InputProps={{
                    readOnly: true
                  }}
                  disabled
                  helperText="Auto-generated" />

                  <TextField
                  label="Scenario Name"
                  fullWidth
                  margin="dense"
                  value={currentScenario.ScenarioName || ''}
                  onChange={(e) => {
                    const newName = e.target.value;
                    setCurrentScenario({ ...currentScenario, ScenarioName: newName });
                    validateScenarioNameUniqueness(newName);
                  }}
                  error={!!scenarioNameError}
                  helperText={scenarioNameError}
                  required
                  autoComplete="off" />

                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <TextField
                    label="Domestic Tax Rate"
                    type="number"
                    fullWidth
                    margin="dense"
                    value={currentScenario.DomesticTaxRate ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCurrentScenario({
                        ...currentScenario,
                        DomesticTaxRate: v === '' ? '' : parseFloat(v)
                      });
                    }}
                    sx={{ flex: 1 }}
                    required
                    autoComplete="off" />

                    <Autocomplete
                    options={COUNTRIES}
                    value={currentScenario.DomicileCountry || ''}
                    onChange={(event, newValue) => {
                      setCurrentScenario({ ...currentScenario, DomicileCountry: newValue || '' });
                    }}
                    renderInput={(params) =>
                    <TextField
                      {...params}
                      label="Domicile Country"
                      margin="dense"
                      autoComplete="off" />

                    }
                    sx={{ flex: 1 }} />

                  </Box>
                  <TextField
                  select
                  label="Point In Time"
                  fullWidth
                  margin="dense"
                  value={currentScenario.PointInTime || ''}
                  onChange={(e) => setCurrentScenario({ ...currentScenario, PointInTime: e.target.value })}
                  required>

                    {pointInTimes.map((pit) =>
                  <MenuItem key={pit.id} value={pit.Code}>
                        {pit.Name}
                      </MenuItem>
                  )}
                  </TextField>
                  <TextField
                  label="Description"
                  fullWidth
                  margin="dense"
                  multiline
                  rows={4}
                  value={currentScenario.Description || ''}
                  onChange={(e) => setCurrentScenario({ ...currentScenario, Description: e.target.value })}
                  autoComplete="off" />

                </>
              }

              {(scenarioType === 'copy' || scenarioType === 'rollover') &&
              <TextField
                select
                fullWidth
                margin="dense"
                value={templateScenarioId}
                onChange={(e) => {
                  setTemplateScenarioId(e.target.value);
                  if (e.target.value) {
                    loadTemplateScenario(e.target.value);
                  }
                }}
                label="Select Template Scenario"
                required>

                  {scenarios.map((scenario) =>
                <MenuItem key={scenario.id} value={scenario.id}>
                      {scenario.ScenarioCode} - {scenario.ScenarioName}
                    </MenuItem>
                )}
                </TextField>
              }

              {(scenarioType === 'copy' || scenarioType === 'rollover') && templateScenarioId &&
              <>
                  <TextField
                  label="Scenario Name"
                  fullWidth
                  margin="dense"
                  value={currentScenario.ScenarioName || ''}
                  onChange={(e) => {
                    const newName = e.target.value;
                    setCurrentScenario({ ...currentScenario, ScenarioName: newName });
                    validateScenarioNameUniqueness(newName);
                  }}
                  error={!!scenarioNameError}
                  helperText={scenarioNameError}
                  required
                  autoComplete="off" />


                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <TextField
                    label="Domestic Tax Rate"
                    sx={{ width: '50%' }}
                    margin="dense"
                    type="number"
                    value={currentScenario.DomesticTaxRate ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCurrentScenario({
                        ...currentScenario,
                        DomesticTaxRate: v === '' ? '' : parseFloat(v)
                      });
                    }}
                    InputProps={{
                      endAdornment: ' %',
                      inputProps: {
                        inputMode: 'decimal'
                      }
                    }}
                    required
                    autoComplete="off" />

                    
                    <Autocomplete
                    options={COUNTRIES}
                    value={currentScenario.DomicileCountry || ''}
                    onChange={(event, newValue) => {
                      setCurrentScenario({ ...currentScenario, DomicileCountry: newValue || '' });
                    }}
                    renderInput={(params) =>
                    <TextField
                      {...params}
                      label="Domicile Country"
                      margin="dense"
                      required
                      autoComplete="off" />

                    }
                    sx={{ width: '50%' }} />

                  </Box>
                  
                  <TextField
                  select
                  label="Point In Time"
                  fullWidth
                  margin="dense"
                  value={currentScenario.PointInTime || ''}
                  onChange={(e) => setCurrentScenario({ ...currentScenario, PointInTime: e.target.value })}
                  required>

                    {pointInTimes.map((pit) =>
                  <MenuItem key={pit.id} value={pit.Code}>
                        {pit.Name}
                      </MenuItem>
                  )}
                  </TextField>
                  <TextField
                  label="Description"
                  fullWidth
                  margin="dense"
                  multiline
                  minRows={3}
                  maxRows={6}
                  value={currentScenario.Description || ''}
                  onChange={(e) => setCurrentScenario({ ...currentScenario, Description: e.target.value })}
                  autoComplete="off" />

                  
                  {/* Associated Entities Section */}
                  
                </>
              }

              {(scenarioType === 'blank' || scenarioType === 'rollForward') &&
              <>
                  <TextField
                  label="Scenario Name"
                  fullWidth
                  margin="dense"
                  value={currentScenario.ScenarioName || ''}
                  onChange={(e) => {
                    const newName = e.target.value;
                    setCurrentScenario({ ...currentScenario, ScenarioName: newName });
                    validateScenarioNameUniqueness(newName);
                  }}
                  error={!!scenarioNameError}
                  helperText={scenarioNameError}
                  required
                  autoComplete="off" />


                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <TextField
                    label="Domestic Tax Rate"
                    sx={{ width: '50%' }}
                    margin="dense"
                    type="number"
                    value={currentScenario.DomesticTaxRate ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCurrentScenario({
                        ...currentScenario,
                        DomesticTaxRate: v === '' ? '' : parseFloat(v)
                      });
                    }}
                    InputProps={{
                      endAdornment: ' %',
                      inputProps: {
                        inputMode: 'decimal'
                      }
                    }}
                    required
                    autoComplete="off" />

                    
                    <Autocomplete
                    options={COUNTRIES}
                    value={currentScenario.DomicileCountry || ''}
                    onChange={(event, newValue) => {
                      setCurrentScenario({ ...currentScenario, DomicileCountry: newValue || '' });
                    }}
                    renderInput={(params) =>
                    <TextField
                      {...params}
                      label="Domicile Country"
                      margin="dense"
                      required
                      autoComplete="off" />

                    }
                    sx={{ width: '50%' }} />

                  </Box>
                  
                  <TextField
                  select
                  label="Point In Time"
                  fullWidth
                  margin="dense"
                  value={currentScenario.PointInTime || ''}
                  onChange={(e) => setCurrentScenario({ ...currentScenario, PointInTime: e.target.value })}
                  required
                  helperText="Select a Point In Time from the list">

                    {pointInTimes.map((pit) =>
                  <MenuItem key={pit.id} value={pit.Code}>
                        {pit.Name}
                      </MenuItem>
                  )}
                  </TextField>
                  <TextField
                  label="Description"
                  fullWidth
                  margin="dense"
                  multiline
                  minRows={3}
                  maxRows={6}
                  value={currentScenario.Description || ''}
                  onChange={(e) => setCurrentScenario({ ...currentScenario, Description: e.target.value })}
                  autoComplete="off" />

                </>
              }
              
              {/* Unified Associated Entities Section - Works for both Create and Edit modes */}
              {currentScenario.PointInTime &&
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Associated Entities
                </Typography>
                
                {/* Show current entities as chips */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {currentScenario.entityList?.map((entity) => {
                    // Handle both object format and string format for backward compatibility
                    const entityCode = typeof entity === 'object' ? entity.EntityCode : entity;
                    const entityName = typeof entity === 'object' ? entity.EntityName : entities.find((e) => e.EntityCode === entity)?.EntityName || entity;

                    return (
                      <Paper key={entityCode} sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5 }}>
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          {entityName}
                        </Typography>
                        <IconButton size="small" color="error" onClick={async () => {
                          const newEntityList = currentScenario.entityList.filter((item) => {
                            const itemCode = typeof item === 'object' ? item.EntityCode : item;
                            return itemCode !== entityCode;
                          });
                          setCurrentScenario({ ...currentScenario, entityList: newEntityList });

                          // If this is an existing scenario (editMode), delete the EntityInput
                          if (editMode && currentScenario.ScenarioCode) {
                            try {


                              // Find and delete the EntityInput record
                              const entityInputsResponse = await api.get('/entity-models/input');
                              const entityInputs = entityInputsResponse.data || [];
                              const entityInputToDelete = entityInputs.find((input) =>
                              input.ScenarioCode === currentScenario.ScenarioCode &&
                              input.EntityCode === entityCode
                              );

                              if (entityInputToDelete) {
                                await api.delete(`/entity-models/input/${entityInputToDelete.id}`);

                                showNotification(`EntityInput removed for ${entityCode}`, 'success');
                              } else {

                              }
                            } catch (error) {
                              console.error('Error deleting EntityInput:', error);
                              showNotification(`Failed to remove EntityInput for ${entityCode}`, 'error');
                            }
                          }
                        }}>
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                      </Paper>);

                  })}
                </Box>
                
                {/* Add new entity */}
                <Grid container spacing={1} alignItems="center">
                  <Grid item>
                    <Autocomplete
                      size="small"
                      options={entities.filter((entity) => {
                        const entityCodes = currentScenario.entityList?.map((item) =>
                        typeof item === 'object' ? item.EntityCode : item
                        ) || [];

                        // Only show entities that match the scenario's PointInTime and are not already selected
                        const matchesPointInTime = entity.PointInTime === currentScenario.PointInTime;
                        const notAlreadySelected = !entityCodes.includes(entity.EntityCode);
                        return matchesPointInTime && notAlreadySelected;
                      }).sort((a, b) => (a.EntityName || '').localeCompare(b.EntityName || ''))}
                      getOptionLabel={(entity) => {
                        const entityPointInTime = pointInTimes.find((pit) => pit.Code === entity.PointInTime);
                        const pointInTimeName = entityPointInTime?.Name || entity.PointInTime || 'N/A';
                        return `${entity.EntityName} (${pointInTimeName})`;
                      }}
                      value={selectedEntity}
                      onChange={(event, newValue) => {
                        setSelectedEntity(newValue);
                      }}
                      renderInput={(params) =>
                      <TextField
                        {...params}
                        label="Select Entity"
                        size="small"
                        variant="outlined"
                        sx={{
                          width: '300px',
                          '& .MuiOutlinedInput-root': {
                            minWidth: '300px'
                          }
                        }} />

                      }
                      loading={loadingEntities}
                      disabled={loadingEntities} />

                  </Grid>
                  <Grid item>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={async () => {
                        if (!selectedEntity) return;

                        const entityCode = selectedEntity.EntityCode;
                        const entityCodes = currentScenario.entityList?.map((item) =>
                        typeof item === 'object' ? item.EntityCode : item
                        ) || [];






                        if (entityCodes.includes(entityCode)) {

                          return;
                        }

                        // Update entity list - add the full entity object for consistency
                        const newEntityList = [...(currentScenario.entityList || []), {
                          EntityCode: selectedEntity.EntityCode,
                          EntityName: selectedEntity.EntityName,
                          Year: selectedEntity.Year,
                          Period: selectedEntity.Period,
                          PointInTime: selectedEntity.PointInTime,
                          PeriodInTime: selectedEntity.PeriodInTime,
                          label: selectedEntity.label
                        }];



                        setCurrentScenario({
                          ...currentScenario,
                          entityList: newEntityList
                        });

                        // If this is an existing scenario (editMode), create the EntityInput
                        if (editMode && currentScenario.ScenarioCode) {
                          try {


                            // Check if entity is active before creating EntityInput
                            const masterEntitiesResponse = await api.get('/entity-models/master');
                            const masterEntities = masterEntitiesResponse.data.data || [];
                            const entityDetails = masterEntities.find((entity) => entity.EntityCode === entityCode);

                            if (!entityDetails) {

                              showNotification(`Entity ${entityCode} not found`, 'error');
                              return;
                            }

                            if (!entityDetails.Active) {

                              showNotification(`Entity ${entityCode} is inactive - EntityInput not created`, 'info');
                              return;
                            }



                            // Check if EntityInput already exists
                            const entityInputsResponse = await api.get('/entity-models/input');
                            const entityInputs = entityInputsResponse.data || [];
                            const existingEntityInput = entityInputs.find((input) =>
                            input.ScenarioCode === currentScenario.ScenarioCode &&
                            input.EntityCode === entityCode
                            );

                            if (!existingEntityInput) {
                              // Create new EntityInput with null rates
                              const { year: eiYear, period: eiPeriod } = function derive(code) {
                                if (!code) return { year: null, period: '' };
                                const m = String(code).toUpperCase().match(/^(\d{4})-(Q[1-4]|ANNUAL)$/);
                                if (m) return { year: parseInt(m[1], 10), period: m[2] };
                                return { year: new Date().getFullYear(), period: 'Q4' };
                              }(currentScenario.PointInTime);
                              const entityInputData = {
                                ScenarioCode: currentScenario.ScenarioCode,
                                EntityCode: entityCode,
                                CurrentTaxRate: null,
                                BeginningDeferredTaxRate: null,
                                EndingDeferredTaxRate: null,
                                AverageFXRate: null,
                                BeginningSpotRate: null,
                                EndingSpotRate: null,
                                Year: eiYear,
                                Period: eiPeriod,
                                dateCreated: new Date().toISOString(),
                                ModifiedOn: new Date().toISOString(),
                                modifiedBy: 'admin'
                              };

                              await api.post('/entity-models/input', entityInputData);

                              showNotification(`EntityInput created for ${entityCode}`, 'success');
                            } else {

                            }
                          } catch (error) {
                            console.error('Error creating EntityInput:', error);
                            showNotification(`Failed to create EntityInput for ${entityCode}`, 'error');
                          }
                        }

                        // Clear selection
                        setSelectedEntity(null);
                      }}
                      disabled={!selectedEntity}
                      sx={{
                        height: '40px'
                      }}>

                      Add
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={async () => {
                        // Get all available entities that match PointInTime and aren't already added
                        const entityCodes = currentScenario.entityList?.map((item) =>
                        typeof item === 'object' ? item.EntityCode : item
                        ) || [];

                        const availableEntities = entities.filter((entity) => {
                          const matchesPointInTime = entity.PointInTime === currentScenario.PointInTime;
                          const notAlreadySelected = !entityCodes.includes(entity.EntityCode);
                          return matchesPointInTime && notAlreadySelected;
                        });

                        if (availableEntities.length === 0) {
                          showNotification('No entities available to add', 'info');
                          return;
                        }



                        // Add all available entities to the list
                        const newEntityList = [
                        ...(currentScenario.entityList || []),
                        ...availableEntities.map((entity) => ({
                          EntityCode: entity.EntityCode,
                          EntityName: entity.EntityName,
                          Year: entity.Year,
                          Period: entity.Period,
                          PointInTime: entity.PointInTime,
                          PeriodInTime: entity.PeriodInTime,
                          label: entity.label
                        }))];


                        setCurrentScenario({
                          ...currentScenario,
                          entityList: newEntityList
                        });

                        // If this is an existing scenario (editMode), create EntityInputs for all new entities
                        if (editMode && currentScenario.ScenarioCode) {
                          try {
                            // Fetch master entities to check active status
                            const masterEntitiesResponse = await api.get('/entity-models/master');
                            const masterEntities = masterEntitiesResponse.data.data || [];

                            // Fetch existing EntityInputs
                            const entityInputsResponse = await api.get('/entity-models/input');
                            const entityInputs = entityInputsResponse.data || [];

                            let created = 0;
                            let skipped = 0;

                            for (const entity of availableEntities) {
                              const entityCode = entity.EntityCode;

                              // Check if entity is active
                              const entityDetails = masterEntities.find((e) => e.EntityCode === entityCode);
                              if (!entityDetails || !entityDetails.Active) {

                                skipped++;
                                continue;
                              }

                              // Check if EntityInput already exists
                              const existingEntityInput = entityInputs.find((input) =>
                              input.ScenarioCode === currentScenario.ScenarioCode &&
                              input.EntityCode === entityCode
                              );

                              if (!existingEntityInput) {
                                // Create new EntityInput
                                const { year: eiYear, period: eiPeriod } = function derive(code) {
                                  if (!code) return { year: null, period: '' };
                                  const m = String(code).toUpperCase().match(/^(\d{4})-(Q[1-4]|ANNUAL)$/);
                                  if (m) return { year: parseInt(m[1], 10), period: m[2] };
                                  return { year: new Date().getFullYear(), period: 'Q4' };
                                }(currentScenario.PointInTime);

                                await api.post('/entity-models/input', {
                                  ScenarioCode: currentScenario.ScenarioCode,
                                  EntityCode: entityCode,
                                  CurrentTaxRate: null,
                                  BeginningDeferredTaxRate: null,
                                  EndingDeferredTaxRate: null,
                                  AverageFXRate: null,
                                  BeginningSpotRate: null,
                                  EndingSpotRate: null,
                                  Year: eiYear,
                                  Period: eiPeriod,
                                  dateCreated: new Date().toISOString(),
                                  ModifiedOn: new Date().toISOString(),
                                  modifiedBy: 'admin'
                                });
                                created++;
                              }
                            }

                            const message = `Added ${availableEntities.length} entities` + (
                            created > 0 ? `, created ${created} EntityInputs` : '') + (
                            skipped > 0 ? `, skipped ${skipped} inactive` : '');
                            showNotification(message, 'success');
                          } catch (error) {
                            console.error('Error creating EntityInputs:', error);
                            showNotification('Added entities but some EntityInputs failed to create', 'warning');
                          }
                        } else {
                          showNotification(`Added ${availableEntities.length} entities`, 'success');
                        }
                      }}
                      disabled={entities.filter((entity) => {
                        const entityCodes = currentScenario.entityList?.map((item) =>
                        typeof item === 'object' ? item.EntityCode : item
                        ) || [];
                        return entity.PointInTime === currentScenario.PointInTime &&
                        !entityCodes.includes(entity.EntityCode);
                      }).length === 0}
                      sx={{
                        height: '40px'
                      }}>

                      Add All
                    </Button>
                  </Grid>
                </Grid>
              </Box>
              }
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!currentScenario.ScenarioName ||
              !currentScenario.PointInTime ||
              !!scenarioNameError ||
              savingScenario}>

              {savingScenario ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Hidden file input for Excel import */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          accept=".xlsx, .xls" />

        
        {importing &&
        <Dialog open={importing} maxWidth="sm">
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 3 }}>Importing Scenarios...</Typography>
            </DialogContent>
          </Dialog>
        }

        {/* Deleting Scenarios Loading Dialog */}
        {deletingScenarios &&
        <Dialog open={deletingScenarios} maxWidth="sm">
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 3 }}>Deleting Scenarios...</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                This may take a moment while we remove all associated data
              </Typography>
            </DialogContent>
          </Dialog>
        }

        {/* Saving Scenario Loading Dialog */}
        {savingScenario &&
        <Dialog open={savingScenario} maxWidth="sm">
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 3 }}>
                {scenarioType === 'copy' ? 'Copying Scenario...' :
              scenarioType === 'rollover' ? 'Rolling Over Scenario...' :
              editMode ? 'Updating Scenario...' : 'Creating Scenario...'}
              </Typography>
              {(scenarioType === 'copy' || scenarioType === 'rollover') &&
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                  This may take a moment while we copy all the data
                </Typography>
            }
            </DialogContent>
          </Dialog>
        }
        
        {/* Notifications */}
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleNotificationClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>

          <Alert
            onClose={handleNotificationClose}
            severity={notification.severity}>

            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>);

};

export default ScenarioManagement;
